<div
  class="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-ink/80 px-4 py-10"
  (click)="close()"
>
  <div
    class="w-full rounded-md border border-hairline bg-panel p-6"
    [class.max-w-lg]="personId() === null"
    [class.max-w-4xl]="personId() !== null"
    (click)="$event.stopPropagation()"
  >
    <div class="mb-4 flex items-start justify-between gap-4">
      <h2 class="font-display text-xl font-semibold text-parchment">
        {{ person()?.name || (personId() === null ? 'Add person' : 'Person') }}
      </h2>
      <button
        type="button"
        (click)="close()"
        class="text-xl leading-none text-dusk hover:text-parchment"
        aria-label="Close"
      >
        &times;
      </button>
    </div>

    @if (loading()) {
      <p class="text-dusk">Loading…</p>
    } @else if (errorMessage()) {
      <p class="rounded border border-wax/40 bg-wax/10 px-4 py-3 text-sm text-parchment">
        {{ errorMessage() }}
      </p>
    } @else {
      <div [class]="personId() === null ? '' : 'grid gap-6 md:grid-cols-[minmax(0,1fr)_17rem]'">
      <div>
      <form [formGroup]="form" (ngSubmit)="save()" class="flex flex-col gap-3">
        @if (person(); as p) {
          <p class="text-xs uppercase tracking-wide text-dusk">
            Status
            <span class="text-parchment">{{ p.status === 'DEAD' ? 'Deceased' : 'Alive' }}</span>
            <span class="text-dusk"> — derived from Date of death, not directly editable</span>
          </p>
        }

        <label class="flex flex-col gap-1 text-sm">
          <span class="text-dusk">Name</span>
          <input
            formControlName="name"
            type="text"
            class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm">
          <span class="text-dusk">Known for <span class="text-dusk/70">(occupation/role, e.g. "American actor")</span></span>
          <input
            formControlName="knownFor"
            type="text"
            class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm">
          <span class="text-dusk">Aliases <span class="text-dusk/70">(comma-separated stage/ring/pen names, e.g. "The Rock")</span></span>
          <input
            formControlName="aliases"
            type="text"
            placeholder="The Rock, Rocky Maivia"
            class="rounded border border-hairline bg-ink px-3 py-2 text-parchment placeholder:text-dusk/60 focus:border-gold focus:outline-none"
          />
        </label>

        <div class="grid grid-cols-2 gap-3">
          <label class="flex flex-col gap-1 text-sm">
            <span class="text-dusk">
              Date of birth
              @if (!isDeceased() && ageText()) {
                <span class="text-dusk/70">{{ ageText() }}</span>
              }
            </span>
            <input
              formControlName="dateOfBirth"
              type="date"
              class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm">
            <span class="text-dusk">
              Date of death
              @if (isDeceased() && ageText()) {
                <span class="text-dusk/70">{{ ageText() }}</span>
              }
            </span>
            <input
              formControlName="dateOfDeath"
              type="date"
              class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
            />
          </label>
        </div>

        <label class="flex flex-col gap-1 text-sm">
          <span class="text-dusk">Cause of death</span>
          <input
            formControlName="causeOfDeath"
            type="text"
            class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm">
          <span class="flex items-center justify-between">
            <span class="text-dusk">Wikipedia URL</span>
            @if (form.controls.wikipediaUrl.value.trim()) {
              <a
                [href]="form.controls.wikipediaUrl.value.trim()"
                target="_blank"
                rel="noopener noreferrer"
                class="text-xs font-semibold text-gold hover:underline"
              >
                Open ↗
              </a>
            }
          </span>
          <input
            formControlName="wikipediaUrl"
            type="text"
            class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm">
          <span class="text-dusk">Image URL</span>
          <div class="flex items-center gap-3">
            <input
              formControlName="imageUrl"
              type="text"
              class="flex-1 rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
            />
            @if (form.controls.imageUrl.value.trim()) {
              <img
                [src]="form.controls.imageUrl.value.trim()"
                alt=""
                class="h-12 w-12 shrink-0 rounded-full border border-hairline object-cover"
              />
            }
          </div>
        </label>

        @if (saveError()) {
          <p class="rounded border border-wax/40 bg-wax/10 px-3 py-2 text-xs text-parchment">
            {{ saveError() }}
          </p>
        }

        <div class="mt-2 flex items-center gap-3">
          <button
            type="submit"
            [disabled]="saving()"
            class="inline-flex items-center justify-center rounded-[3px] bg-gradient-to-b from-gold to-gold-dim px-5 py-2 text-sm font-bold text-ink transition hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60"
          >
            {{ person() ? (saving() ? 'Saving…' : 'Save') : (saving() ? 'Adding…' : 'Add') }}
          </button>
        </div>
      </form>

      @if (person()) {
        <div class="mt-6 border-t border-hairline pt-4">
          @if (deleteError()) {
            <p class="mb-3 rounded border border-wax/40 bg-wax/10 px-3 py-2 text-xs text-parchment">
              {{ deleteError() }}
            </p>
          }
          @if (!confirmingDelete()) {
            <button
              type="button"
              (click)="confirmingDelete.set(true)"
              class="text-xs text-wax-bright hover:underline"
            >
              Delete this person
            </button>
          } @else {
            <div class="flex items-center gap-3 text-xs">
              <span class="text-parchment">Delete permanently? This can't be undone.</span>
              <button
                type="button"
                (click)="confirmDelete()"
                [disabled]="deleting()"
                class="rounded-[3px] border border-wax-bright px-3 py-1.5 font-semibold text-wax-bright transition hover:bg-wax/10 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {{ deleting() ? 'Deleting…' : 'Yes, delete' }}
              </button>
              <button
                type="button"
                (click)="confirmingDelete.set(false)"
                class="text-dusk hover:text-parchment"
              >
                Cancel
              </button>
            </div>
          }
        </div>
      }
      </div>

      @if (personId() !== null) {
        <aside
          class="rounded border border-hairline bg-ink/40 p-4 md:max-h-[34rem] md:overflow-y-auto"
          aria-label="Contestants who picked this person"
        >
          <h3 class="text-xs font-semibold uppercase tracking-wide text-dusk">
            Picked by
            @if (!pickersLoading() && !pickersError()) {
              <span class="text-parchment">({{ pickers().length }})</span>
            }
          </h3>

          @if (pickersLoading()) {
            <p class="mt-3 text-sm text-dusk">Loading…</p>
          } @else if (pickersError()) {
            <p class="mt-3 rounded border border-wax/40 bg-wax/10 px-3 py-2 text-xs text-parchment">
              {{ pickersError() }}
            </p>
          } @else if (pickerGroups().length === 0) {
            <p class="mt-3 text-sm text-dusk">Nobody has picked this person.</p>
          } @else {
            @for (group of pickerGroups(); track group.competition.id) {
              <div class="mt-4">
                <a
                  [routerLink]="['/competitions', group.competition.id]"
                  class="text-sm font-semibold text-parchment hover:underline"
                  >{{ group.competition.name }}</a
                >
                <p class="text-xs text-dusk">{{ competitionDates(group.competition) }}</p>
                <ul class="mt-1.5 flex flex-col gap-0.5 text-sm text-parchment">
                  @for (c of group.contestants; track $index) {
                    <li>
                      {{ c.displayName }}
                      @if (c.isAlternate) {
                        <span class="text-xs text-dusk">(alternate)</span>
                      }
                    </li>
                  }
                </ul>
              </div>
            }
          }
        </aside>
      }
      </div>
    }
  </div>
</div>
